import {
  PreviousTotalsOverlay,
  PreviousTotalsOverlayBackgroundColors,
  PreviousTotalsOverlayThemeOption,
  toPreviousTotalsOverlayBackgroundColor,
} from "@playlive/overlay-core/overlays/previous-totals";
import { deepSignal } from "deepsignal";
import NumberInput from "@/components/input/number.input.jsx";
import Picker from "@/components/input/picker.input.jsx";
import CustomizeSection from "@/components/overlays/shared/customize-section.jsx";
import LinkSetup from "@/components/overlays/social/link-setup/link-setup.jsx";
import HorizontalCustomizeWrapper from "@/components/overlays/wrapper/horizonal-customize-wrapper.jsx";
import ScaledOverlayWrapper from "@/components/overlays/wrapper/scaled-overlay-wrapper.jsx";
import ColorPicker from "@/components/pickers/color-picker/color.picker.jsx";
import ColorPickerLabel from "@/components/pickers/color-picker/color-picker-label.jsx";
import { previous } from "@/data/example/index.js";
import useCommon from "@/hooks/useCommon.js";
import { isYes } from "@/util/common.js";
import { CHARITY } from "@/util/const.js";

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  previousTotalsOverlayTheme: PreviousTotalsOverlayThemeOption.CLASSIC,
  previousTotalsOverlayBackgroundColor: "default-red",
  previousTotalsOverlayMaxVisible: 5,
});

const useBetaProgressBars = isYes(import.meta.env.VITE_BETA_PROGRESS_BARS || "no");
const urlBase = useBetaProgressBars ? "overlays/previous-totals" : "previous-totals";

export default function PreviousTotalsOverlayPage() {
  const { overlayInfo, overlayURL, setConfigState } = useCommon({
    config,
    urlBase,
    forceTiltify: true,
    addTiltifyCampaign: false,
  });

  const themeOptions = [
    {
      name: "Slanted",
      value: PreviousTotalsOverlayThemeOption.SLANTED,
    },
    {
      name: "Rounded",
      value: PreviousTotalsOverlayThemeOption.ROUNDED,
    },
    {
      name: "Classic",
      value: PreviousTotalsOverlayThemeOption.CLASSIC,
    },
  ];

  function filterColors(color) {
    return PreviousTotalsOverlayBackgroundColors.find((c) => c === color);
  }

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col xl:flex-row overflow-x-hidden">
        <div className="example_overlay overflow-x-hidden w-full min-h-72">
          <ScaledOverlayWrapper
            className="min-w-[570px]"
            allowScaling={false}
            defaultScale={"scale-100"}
          >
            <PreviousTotalsOverlay
              backgroundColor={toPreviousTotalsOverlayBackgroundColor(
                config.previousTotalsOverlayBackgroundColor,
              )}
              demoMode
              theme={config.previousTotalsOverlayTheme}
              items={previous}
              maxVisible={config.previousTotalsOverlayMaxVisible}
            />
          </ScaledOverlayWrapper>
        </div>

        <HorizontalCustomizeWrapper className="flex flex-col mb-0!">
          <CustomizeSection title="Appearance" className="flex-grow">
            <ColorPickerLabel color={config.previousTotalsOverlayBackgroundColor} />

            <ColorPicker
              fieldName="previousTotalsOverlayBackgroundColor"
              currentColor={config.previousTotalsOverlayBackgroundColor}
              filterFunction={filterColors}
              onChange={setConfigState}
            />

            <Picker
              items={themeOptions}
              fieldName="previousTotalsOverlayTheme"
              onChange={setConfigState}
              currentValue={config.previousTotalsOverlayTheme}
              labelText="Theme"
              className="mb-2"
            />

            <NumberInput
              name="previousTotalsOverlayMaxVisible"
              value={Number(config.previousTotalsOverlayMaxVisible)}
              placeholder="5"
              onChange={setConfigState}
              isCurrency={false}
              maxValue={5}
              minValue={1}
              suffix={config.$previousTotalsOverlayMaxVisible === 1 ? "campaign" : "campaigns"}
              labelText="Campaign Count"
              description="Amount of campaigns to display"
            />
          </CustomizeSection>

          {/* Charity Section Placeholder */}
        </HorizontalCustomizeWrapper>
      </div>

      <LinkSetup
        allowTeamCampaignSelection={true}
        showTiltifyCampaignSelector={false}
        constrainWidth={false}
        charityType={CHARITY.TILTIFY}
        obsDimensions={overlayInfo.obsDimensions}
        overlayName={overlayInfo.name}
        overlayURL={overlayURL}
        finalStepSlotPosition="middle"
      />
    </div>
  );
}
